import { roles } from 'src/app/shared/lists/roles';
import { environment } from "src/environments/environment";
import { Company, COMPANY_STATE, FRAGA_CATALOG } from "../../../../interface/company";
import { MkgUser } from "../../../../interface/user";
import { CreditReleaseUtilities } from '../../../../class/credit-release-utilities';
import { Params } from '@angular/router';

export interface OldSideMenuChild {
    component: string;
    href: string[];
    queryParams?: Params;
    shadows?: string[];
    inDevelopment?: boolean;
    /** Chave única, ou lista de chaves (libera quando qualquer uma casar — ver `CanAccessPipe`). */
    profileRule?: string | string[];
    blockedRoles?: number[];
    hideInProduction?: boolean;
  }

export interface OldSideMenu {
  component: string;
  icon?: string;
  svgIcon?: string;
  href?: string[];
  queryParams?: Params;
  expanded?: boolean;
  childs?: OldSideMenuChild[];
  shadows?: string[];
  /** Module requested in user AccesProfile to allow access this menu */
  profileModule?: string;
  /** user roles wich can access this menu */
  blockedRoles?: number[];
  /** Exceção ao perfil de acesso: aparece por papel mesmo com perfil (ver `SideMenuChild`). */
  bypassAccessProfile?: boolean;
  inDevelopment?: boolean;
  hideInProduction?: boolean;
}


export const OLD_MEKANIC_MENU: (company: Company, user: MkgUser) => OldSideMenu[] = (company, user) => {

  const isContaAzul = Number(company.currentState) === COMPANY_STATE.CONTA_AZUL;
  // Liberações por títulos vencidos (#73105): administrador com a flag ligada. Exceção ao perfil de acesso (D16)
  const canManageCreditRelease = CreditReleaseUtilities.canManage(company, user);

  const menus: OldSideMenu[] = [
    // company
    {
      component: 'company',
      href: ['/company'],
      icon: 'domain',
    },

    // quick access of favorite menus
    {
      component: 'favorites',
      icon: 'star',
      childs: [
        {
          component: 'configure',
          href: ['company', company.id, 'quick-access']
        }
      ]
    },
    // dashboard kpi
    {
      component: 'kpi-dashboard',
      profileModule: "KPI",
      icon: 'dashboard',
      blockedRoles: [roles.mechanic.id],
      href: ['company', company.id, 'kpi']
    },
    // gestao do patio
    {
      component: 'karban',
      profileModule: "YARD",
      icon: 'home',
      blockedRoles: [],
      href: ['company', company.id, 'home']
    },

    // budgets
    {
      component: 'budgets',
      profileModule: "OS",
      blockedRoles: [roles.mechanic.id],
      icon: 'assignment',
      shadows: ['budget-create'],
      childs: [
        {
          component: 'budget',
          shadows: ['budget_detail', 'ro_create'],
          profileRule: "OS.list.budget",
          href: ['company', company.id, 'os', 'budget']
        },
        {
          component: 'rejected',
          shadows: ['rejected_detail'],
          profileRule: "OS.list.rejectedBudget",
          href: ['company', company.id, 'os', 'rejected']
        },
        {
          component: 'closed-budget',
          shadows: ['closed-budget_detail'],
          profileRule: "OS.list.closedBudget",
          href: ['company', company.id, 'os', 'closed-budget']
        }
      ]
    },

    // OSs
    {
      component: 'ro',
      profileModule: "OS",
      blockedRoles: [roles.mechanic.id],
      icon: 'assignment_turned_in',
      shadows: ['ro-create'],
      childs: [
        {
          component: 'opened',
          profileRule: "OS.list.openedRo",
          shadows: ['opened_detail'],
          href: ['company', company.id, 'os', 'opened']
        },
        {
          component: 'closed',
          profileRule: "OS.list.closedRo",
          shadows: ['closed_detail'],
          href: ['company', company.id, 'os', 'closed']
        },
        {
          component: 'ended',
          profileRule: "OS.list.endedRo",
          shadows: ['ended_detail'],
          href: ['company', company.id, 'os', 'ended']
        }
      ]
    },

    // calendar
    {
      component: 'calendar',
      profileModule: "CALENDAR",
      icon: 'today',
      blockedRoles: [roles.mechanic.id],
      href: ['company', company.id, 'calendar']
    },

    // CRM
    {
      component: 'CRM',
      profileModule: "CRM",
      blockedRoles: [roles.mechanic.id],
      icon: 'reduce_capacity',
      childs: [
        {
          component: 'lead-list',
          profileRule: "CRM.leads.read",
          href: ['company', company.id, 'crm', 'lead-list']
        },
        {
          component: 'contact-list',
          profileRule: "CRM.contacts.read",
          href: ['company', company.id, 'crm', 'contact-list']
        },
        {
          component: 'crm-events',
          profileRule: "CRM.events.read",
          href: ['company', company.id, 'crm', 'events']
        },
        {
          component: 'crm-dashboard',
          profileRule: "CRM.dashboard",
          href: ['company', company.id, 'crm', 'dashboard']
        }
      ]
    },

    // clients
    {
      component: 'clients',
      profileModule: "CLIENT",
      blockedRoles: [roles.mechanic.id],
      icon: 'assignment_ind',
      href: ['company', company.id, 'clients'],
      shadows: ['client-detail']
    },

    // vehicles
    {
      component: environment.agro ? 'machines' : (environment.nautical ? 'boats' : 'vehicles'),
      profileModule: "VEHICLE",
      blockedRoles: [roles.mechanic.id],
      icon: environment.agro ? "agriculture" : (environment.nautical ? "sailing" : "directions_car"),
      href: ['company', company.id, 'vehicles'],
      shadows: ['vehicle-detail', 'search-vehicle']
    },

    // parts
    {
      component: 'parts',
      profileModule: "PART",
      blockedRoles: [roles.mechanic.id],
      icon: 'settings',
      href: ['company', company.id, 'parts'],
      shadows: ['part-detail'],
      childs: [
        {
          component: 'maintenance',
          profileRule: "PART.update",
          href: ['company', company.id, 'parts', 'maintenance']
        },
        ...(company.fragaCatalog && (company.fragaCatalog !== FRAGA_CATALOG.BLOCKED) ? [{
          component: 'parts-consult',
          href: ['company', company.id, 'parts-consult'],
          profileRule: "PART.partsConsult"
        }] : [])
      ]
    },

    // labors
    {
      component: 'labors',
      profileModule: "LABOR",
      blockedRoles: [roles.mechanic.id],
      svgIcon: 'labor',
      href: ['company', company.id, 'labors'],
      shadows: ['labor-detail']
    },

    // cash (financial)
    ...(isContaAzul ? [] : [{
      component: 'cash',
      profileModule: "FINANCIAL",
      blockedRoles: [roles.mechanic.id, roles.consultant.id],
      icon: 'local_atm',
      childs: [
        {
          component: 'cash-title',
          profileRule: "FINANCIAL.titles.read",
          href: ['company', company.id, 'financial', 'cash-title'],
          shadows: ['cash-title-detail']
        },
        {
          component: 'receipt',
          profileRule: "FINANCIAL.cashier.read",
          href: ['company', company.id, 'financial', 'receipt'],
          shadows: ['closed-cashier']
        },
        {
          component: 'banks',
          profileRule: "FINANCIAL.banks.read",
          href: ['company', company.id, 'financial', 'banks']
        },
        {
          component: 'conciliation',
          profileRule: "FINANCIAL.conciliation.read",
          href: ['company', company.id, 'financial', company.useNewBankReconciliation ? 'bank-reconciliation' : 'conciliation'],
          shadows: ['conciliation-detail']
        },
        {
          component: 'detached',
          profileRule: "FINANCIAL.detached.read",
          href: ['company', company.id, 'financial', 'detached']
        },
        {
          component: 'cash-flow-control',
          profileRule: "FINANCIAL.cash-flow-control.read",
          href: ['company', company.id, 'financial', 'cash-flow-control']
        }
      ]
    }]),

    // suppliers
    {
      component: 'suppliers',
      profileModule: "SUPPLIER",
      blockedRoles: [roles.mechanic.id],
      icon: 'local_shipping',
      href: ['company', company.id, 'suppliers'],
      shadows: ['supplier-detail']
    },

    // orders
    {
      component: 'orders',
      profileModule: "ORDER",
      blockedRoles: [roles.mechanic.id],
      icon: 'ballot',
      href: ['company', company.id, 'order'],
      shadows: ['order-detail']
    },

    // users
    {
      component: 'users',
      profileModule: "USER",
      blockedRoles: [roles.mechanic.id, roles.consultant.id],
      icon: 'people',
      childs: [
        {
          profileRule: "USER.REGISTERED_USERS.read",
          component: 'user-list',
          href: ['company', company.id, 'users'],
          shadows: ['user-detail'],
        },

        // access-profiles
        {
          profileRule: "USER.ACCESS_CONTROL.read",
          component: 'access-profile',
          href: ['company', company.id, 'access-profile'],
          shadows: ['access-profile-edit']
        }
      ]
    },


    // invoices
    {
      component: 'invoices',
      profileModule: "INVOICE",
      blockedRoles: [roles.mechanic.id],
      icon: 'receipt',
      href: ['company', company.id, 'invoices'],
      shadows: ['invoice-detail', 'devolution'],
      childs: [
          ...(company.importador === 2 ? [{
          component: 'dfe-list',
          profileRule: "INVOICE.dfe",
          href: ['company', company.id, 'invoices', 'dfe']
        }] : []),
        {
          component: 'xml',
          profileRule: "INVOICE.importXML",
          href: ['company', company.id, 'invoices', 'xml']
        },
        {
          component: 'widthoutXML',
          profileRule: "INVOICE.importWidthoutXML",
          href: ['company', company.id, 'invoices', 'nf'],
          queryParams: { gerencial: "OIM" }
        },
        {
          component: 'sintegra',
          profileRule: "SINTEGRA",
          href: ['company', company.id, 'sintegra'],
          shadows: ['sintegra-detail']
        }
      ]
    },

    // operations
    {
      component: 'operations',
      profileModule: "RECORDS",
      blockedRoles: [roles.mechanic.id, roles.consultant.id],
      icon: 'category',
      href: ['company', company.id, 'operations'],
      shadows: ['operation-detail']
    },

    // stock
    {
      component: 'stock',
      profileModule: "STOCK",
      blockedRoles: [roles.mechanic.id],
      icon: 'widgets',
      href: ['company', company.id, 'stock']
    },

    // dashboard
    {
      component: 'indicators',
      profileModule: "BUSINESS_MANAGEMENT",
      blockedRoles: [roles.mechanic.id, roles.consultant.id],
      icon: 'pie_chart',
      childs: [
        {
          component: 'info',
          href: ['company', company.id, 'business-management'],
          profileRule: "BUSINESS_MANAGEMENT.indicators.read",
          shadows: [
            'info',
            'revenue-indicators-sales',
            'ranking-sellers-average-ticket',
            'ranking-sellers-clients-served',
            'ranking-sellers-sales',
            'revenue-indicators-average-ticket',
            'revenue-indicators-groups',
          ]
        },
        {
          component: 'sales-dashboard',
          profileRule: "BUSINESS_MANAGEMENT.salesIndicators.read",
          href: ['company', company.id, 'business-management', 'sales-dashboard']
        }
      ]
    },

    // Discount Control
    {
      component: 'discount-control',
      blockedRoles: [roles.mechanic.id, roles.consultant.id],
      profileModule: "DISCOUNT_CONTROL",
      icon: 'notifications_active',
      href: ['company', company.id, 'discount-control']
    },

    // Liberações por títulos vencidos (#73105). Exceção ao perfil de acesso: só administrador, com ou sem perfil (D16)
    ...(canManageCreditRelease ? [{
      component: 'credit-release',
      blockedRoles: [roles.manager.id, roles.mechanic.id, roles.consultant.id],
      bypassAccessProfile: true,
      icon: 'lock_open',
      href: ['company', company.id, 'credit-release']
    }] : []),

    // reports
    {
      component: 'reports',
      profileModule: "REPORTS",
      blockedRoles: [roles.mechanic.id, roles.consultant.id],
      icon: 'description',
      childs: [
        {
          component: 'budget-prospection',
          profileRule: 'REPORTS.prospection',
          href: ['company', company.id, 'reports', 'budget-prospection']
        },
        {
          component: 'commissions',
          profileRule: 'REPORTS.commissions',
          href: ['company', company.id, 'reports', 'commissions']
        },
        {
          component: 'dashboard',
          profileRule: 'REPORTS.dashboard',
          href: ['company', company.id, 'reports', 'dashboard']
        },
        {
          component: 'sales',
          profileRule: 'REPORTS.sales',
          href: ['company', company.id, 'reports', 'sales']
        },
        {
          component: 'user-sales',
          profileRule: 'REPORTS.userSales',
          href: ['company', company.id, 'reports', 'user-sales']
        },
        {
          component: 'profits',
          profileRule: 'REPORTS.profits',
          href: ['company', company.id, 'reports', 'profits']
        },
        {
          component: 'profits-by-os',
          profileRule: 'REPORTS.profitsByOs',
          href: ['company', company.id, 'reports', 'profits-by-os']
        },
        {
          component: 'mechanic-time',
          profileRule: 'REPORTS.mechanicTime',
          href: ['company', company.id, 'reports', 'mechanic-time']
        },
        ...(isContaAzul ? [] : [{
          component: 'dre',
          profileRule: 'REPORTS.dre',
          href: ['company', company.id, 'reports', 'dre']
        }]),
        {
          component: 'os-vehicle-history',
          profileRule: 'REPORTS.osVehicleHistory',
          href: ['company', company.id, 'reports', 'os-vehicle-history']
        },
        {
          component: 'vehicle-history',
          profileRule: 'REPORTS.vehicleHistory',
          href: ['company', company.id, 'reports', 'vehicle-history']
        },
        {
          component: 'os-history',
          profileRule: 'REPORTS.osHistory',
          href: ['company', company.id, 'reports', 'os-history']
        },
        {
          component: 'infocar-queries',
          profileRule: "REPORTS.infocarQueries",
          href: ['company', company.id, 'reports', 'infocar-queries']
        },
        {
          component: 'sales-analysis',
          profileRule: "REPORTS.salesAnalysis",
          href: ['company', company.id, 'reports', 'sales-analysis']
        },
        {
          component: 'parts-report',
          profileRule: "REPORTS.parts",
          href: ['company', company.id, 'reports', 'parts-reports']
        },
        {
          component: 'labors-report',
          profileRule: "REPORTS.laborsReports",
          href: ['company', company.id, 'reports', 'labors-reports']
        }
      ]
    },
    /// APONTAMENTO DE SERVICO MECANICO (nova tela)
    {
      component: 'mechanical-control-service',
      profileModule: "NOTATION",
      icon: 'handyman', // build, engineering e handyman
      href: ['company', company.id, 'mechanical'],
      shadows: ['mechanical-service'],
      childs: [
        {
          component: 'app-page-dash-labors',
          href: ['company', company.id, 'mechanical', 'dashboard'],
          profileRule: "NOTATION.dashboard",
          blockedRoles: [roles.mechanic.id, roles.consultant.id]
        }
      ]
    },
    // records
    {
      component: 'records',
      profileModule: "RECORDS",
      blockedRoles: [roles.mechanic.id, roles.consultant.id],
      icon: 'note_add',
      childs: [
        {
          component: 'application-list',
          profileRule: "RECORDS.applications.read",
          href: ['company', company.id, 'records', 'applications']
        },
        {
          component: 'bank-list',
          profileRule: "RECORDS.banks.read",
          href: ['company', company.id, 'records', 'banks']
        },
        {
          component: 'carte-list',
          profileRule: "RECORDS.cartes.read",
          href: ['company', company.id, 'records', 'carte-list']
        },
        // {
        //   component: 'additional-item',
        //   profileRule: "RECORDS.cartes.update",
        //   href: ['company', company.id, 'records', 'additional-item']
        // },
        // {
        //   component: 'parcels',
        //   profileRule: "RECORDS.cartes.update",
        //   href: ['company', company.id, 'records', 'parcels']
        // },
        {
          component: 'checklist',
          profileRule: "RECORDS.mkgChecklist.read",
          href: ['company', company.id, 'records', 'checklist']
        },
        {
          component: 'checklist-models',
          profileRule: "RECORDS.checklistByModel.read",
          href: ['company', company.id, 'records', 'checklist-model']
        },
        {
          component: 'payment-condition-list',
          profileRule: "RECORDS.paymentConditions.read",
          href: ['company', company.id, 'records', 'payment-conditions']
        },
        {
          component: 'custom-state-list',
          profileRule: "RECORDS.additionalOSStates.read",
          href: ['company', company.id, 'records', 'custom-states']
        },
        {
          component: 'custom-state-budget-list',
          profileRule: "RECORDS.additionalBudgetStates.read",
          href: ['company', company.id, 'records', 'custom-states-budget']
        },
        {
          component: 'cashier-event-list',
          profileRule: "RECORDS.events.read",
          href: ['company', company.id, 'records', 'cashier-events']
        },
        {
          component: 'group-list',
          profileRule: "RECORDS.groups.read",
          href: ['company', company.id, 'records', 'groups']
        },
        {
          component: 'groups-by-expense-type-list',
          profileRule: "RECORDS.expenseGroups.read",
          href: ['company', company.id, 'records', 'groups-by-expense-type']
        },
        {
          component: 'brand-list',
          profileRule: "RECORDS.brands.read",
          href: ['company', company.id, 'records', 'brands']
        },
        {
          component: 'origin-list',
          profileRule: "RECORDS.origins.read",
          href: ['company', company.id, 'records', 'origin-list']
        },
        {
          component: 'tags',
          profileRule: "RECORDS.tags.read",
          href: ['company', company.id, 'records', 'tags']
        },
        {
          component: 'survey-list',
          profileRule: "RECORDS.surveys.read",
          href: ['company', company.id, 'records', 'survey-list'], shadows: ['survey-edit']
        },
        {
          component: 'subgroup-list',
          profileRule: "RECORDS.subgroups.read",
          href: ['company', company.id, 'records', 'subgroups']
        },
        {
          component: 'subgroup-by-expense-type-list',
          profileRule: "RECORDS.expenseSubgroups.read",
          href: ['company', company.id, 'records', 'subgroup-by-expense-type']
        },
        {
          component: 'calendar-class',
          profileRule: "RECORDS.calendarTypes.read",
          href: ['company', company.id, 'calendar', 'classList']
        },
        {
          component: 'expense-list',
          profileRule: "RECORDS.expenseTypes.read",
          href: ['company', company.id, 'records', 'expense-types']
        },
        {
          component: 'ro-types',
          profileRule: "RECORDS.OStypes.read",
          href: ['company', company.id, 'records', 'ro-types']
        },
        {
          component: 'reject-types',
          href: ['company', company.id, 'records', 'reject-types'],
          profileRule: "RECORDS.rejectTypes.read"
        },
        {
          component: 'labor-types-list',
          profileRule: "RECORDS.laborTypes.read",
          href: ['company', company.id, 'records', 'labor-types']
        },
        {
          component: 'title-type-list',
          profileRule: "RECORDS.titleTypes.read",
          href: ['company', company.id, 'records', 'title-types']
        },
        {
          component: 'control-messages',
          profileRule: "RECORDS.controlMessages.read",
          href: ['company', company.id, 'records', 'control-messages']
        },
      ]
    },

    // logs
    {
      component: 'logs',
      hideInProduction: true,
      icon: 'view_timeline',
      childs: [
        {
          component: 'logs-client-supplier',
          hideInProduction: true,
          href: ['company', company.id, 'logs', 'client-supplier']
        },
        {
          component: 'logs-diagweb',
          hideInProduction: true,
          href: ['company', company.id, 'logs', 'diagweb']
        },
        {
          component: 'title-logs',
          hideInProduction: true,
          href: ['company', company.id, 'financial', 'title-logs']
        },
        {
          component: 'cashier-logs',
          hideInProduction: true,
          href: ['company', company.id, 'financial', 'cashier-logs']
        },
        ...(company.boletos === 2 && !isContaAzul ? [{
          component: 'boleto-logs',
          hideInProduction: true,
          href: ['company', company.id, 'logs', 'boleto-logs']
        }] : []),
        // Tecinco-internal only: storage error tracking.
        ...(user.isTecincoMember ? [{
          component: 'logs-storage',
          hideInProduction: true,
          href: ['company', company.id, 'logs', 'logs-storage']
        }] : []),
        ...(user.isTecincoMember ? [{
          component: 'operation-logs',
          href: ['company', company.id, 'logs', 'operation']
        }] : []),
        // Auditorias de OS/orçamento e de notas: uso interno do suporte Tecinco.
        ...(user.isTecincoMember ? [{
          component: 'os-audit',
          href: ['company', company.id, 'os', 'audit']
        }] : []),
        ...(user.isTecincoMember ? [{
          component: 'audit-invoices',
          href: ['company', company.id, 'invoices', 'audit']
        }] : []),
      ]
    },
    // settings
    {
      component: 'settings',
      blockedRoles: [roles.mechanic.id],
      icon: 'build',
      href: ['company', company.id, 'settings'],
      childs: [
        {
          component: 'import-csv-xls',
          href: ['company', company.id, 'settings', 'import-ibpt'],
          blockedRoles: [roles.mechanic.id, roles.manager.id, roles.consultant.id]
        },
        // A Tecinco habilita o módulo pelo "Usar TEF"; a oficina configura a própria integração.
        ...(company.useTef ? [{
          component: 'tef-config',
          href: ['company', company.id, 'settings', 'tef'],
          profileRule: "SETTINGS.tef",
          blockedRoles: [roles.mechanic.id, roles.consultant.id]
        }] : []),
      ]
    },
  ];

  if (company.boletos === 2 && !isContaAzul) {
    const recordsIndex = menus.findIndex(m => m.component === 'records');
    const financialIndex = menus.findIndex(m => m.component === 'cash');
    if (recordsIndex !== -1) {
      menus[recordsIndex].childs.push({
        component: 'pjbank-accounts',
        profileRule: "RECORDS.boleto_accounts.read",
        shadows: ['pj_bank'],
        href: ['company', company.id, 'records', 'pjbank-accounts']
      })

      menus[recordsIndex].childs.push(
        {
          component: 'boleto-settings',
          profileRule: "RECORDS.boleto_settings.read",
          shadows: ['boleto_settings_pjbank'],
          href: ['company', company.id, 'records', 'boleto-settings']
        },
      )
    }

    if (financialIndex !== -1) {
      menus[financialIndex].childs.push({
        component: 'boletos',
        profileRule: "FINANCIAL.boleto.read",
        shadows: ['boleto_pjbank'],
        href: ['company', company.id, 'financial', 'boletos']
      })
    }
  }

  if (Array.isArray(user.quickAccess) && user.quickAccess.length) {
    const favoriteIndex = menus.findIndex(m => m.component === 'favorites');
    const configureChild = menus[favoriteIndex].childs.pop();
    const quickchilds: OldSideMenu['childs'] = [];
    for (const quick of user.quickAccess) {
      let child: OldSideMenu['childs'][0];
      for (const menu of menus) {
        if (menu.component === quick) {
          child = {
            component: quick,
            href: menu.href
          }
          continue
        } else if (menu.childs) {
          for (const menuChild of menu.childs) {
            if (menuChild.component === quick) {
              child = menuChild;
              continue;
            }
          }
        }
      }

      if (child) {
        quickchilds.push(child);
      }
    }

    quickchilds.push(configureChild);

    menus[favoriteIndex].childs = quickchilds;
  }

  return menus
}
